import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Canadian percentage marks and letter grades → 4.0 GPA.
 *
 * Canada has no national scale; each university publishes its own, and a graduate or
 * professional-school application service may re-convert the grades. Two published scales are
 * offered (retrieved October 2026):
 *
 * - "uoft": University of Toronto, as published by University of Toronto Mississauga,
 *   Office of the Registrar, "Final Grades" (https://www.utm.utoronto.ca/registrar/academics/grades):
 *   90–100 A+ 4.0; 85–89 A 4.0; 80–84 A− 3.7; 77–79 B+ 3.3; 73–76 B 3.0; 70–72 B− 2.7;
 *   67–69 C+ 2.3; 63–66 C 2.0; 60–62 C− 1.7; 57–59 D+ 1.3; 53–56 D 1.0; 50–52 D− 0.7; 0–49 F 0.0.
 *   This is the usual "Ontario 4.0" reference.
 * - "ubc-alberta": UBC Vancouver publishes percentage bands only — UBC Academic Calendar,
 *   "Grading Practices: Introduction" (https://vancouver.calendar.ubc.ca/campus-wide-policies-and-regulations/grading-practices/introduction):
 *   90–100 A+; 85–89 A; 80–84 A−; 76–79 B+; 72–75 B; 68–71 B−; 64–67 C+; 60–63 C; 55–59 C−;
 *   50–54 D; 0–49 F. It does not publish grade points, and the University of Alberta's Registrar,
 *   "Grade Comparison Guide" (https://www.preview.ualberta.ca/registrar/examinations/assessment-and-grading/grade-comparison-guide.html),
 *   publishes the four-point values for letters only (A+ and A 4.0; A− 3.7; B+ 3.3; B 3.0; B− 2.7;
 *   C+ 2.3; C 2.0; C− 1.7; D+ 1.3; D 1.0; F 0.0) and no percentages. This scale applies Alberta's
 *   letter values to UBC's percentage bands, and says so. Alberta's own letters work directly.
 *
 * The OMSAS 4.0 conversion for Ontario medical-school applications is university-specific and its
 * table could not be retrieved, so it is not offered. Every table here is a reference scale; the
 * receiving institution, or the application service it uses, decides.
 *
 * A mark takes the band whose lower bound it meets, so 76.5 on the Toronto scale is a B.
 */

export type CanadianScale = "uoft" | "ubc-alberta";
export const CANADIAN_SCALES: readonly CanadianScale[] = [
  "uoft",
  "ubc-alberta",
];

export interface CanadianBand {
  min: number;
  letter: string;
  points: number;
}

const UOFT: readonly CanadianBand[] = [
  { min: 90, letter: "A+", points: 4 },
  { min: 85, letter: "A", points: 4 },
  { min: 80, letter: "A−", points: 3.7 },
  { min: 77, letter: "B+", points: 3.3 },
  { min: 73, letter: "B", points: 3 },
  { min: 70, letter: "B−", points: 2.7 },
  { min: 67, letter: "C+", points: 2.3 },
  { min: 63, letter: "C", points: 2 },
  { min: 60, letter: "C−", points: 1.7 },
  { min: 57, letter: "D+", points: 1.3 },
  { min: 53, letter: "D", points: 1 },
  { min: 50, letter: "D−", points: 0.7 },
  { min: 0, letter: "F", points: 0 },
];

/** Alberta's published letter → points. */
const ALBERTA_POINTS: Record<string, number> = {
  "A+": 4,
  A: 4,
  "A−": 3.7,
  "B+": 3.3,
  B: 3,
  "B−": 2.7,
  "C+": 2.3,
  C: 2,
  "C−": 1.7,
  "D+": 1.3,
  D: 1,
  F: 0,
};

/** UBC's percentage bands, scored with Alberta's letter values. */
const UBC_ALBERTA: readonly CanadianBand[] = [
  { min: 90, letter: "A+", points: ALBERTA_POINTS["A+"] ?? 0 },
  { min: 85, letter: "A", points: ALBERTA_POINTS.A ?? 0 },
  { min: 80, letter: "A−", points: ALBERTA_POINTS["A−"] ?? 0 },
  { min: 76, letter: "B+", points: ALBERTA_POINTS["B+"] ?? 0 },
  { min: 72, letter: "B", points: ALBERTA_POINTS.B ?? 0 },
  { min: 68, letter: "B−", points: ALBERTA_POINTS["B−"] ?? 0 },
  { min: 64, letter: "C+", points: ALBERTA_POINTS["C+"] ?? 0 },
  { min: 60, letter: "C", points: ALBERTA_POINTS.C ?? 0 },
  { min: 55, letter: "C−", points: ALBERTA_POINTS["C−"] ?? 0 },
  { min: 50, letter: "D", points: ALBERTA_POINTS.D ?? 0 },
  { min: 0, letter: "F", points: ALBERTA_POINTS.F ?? 0 },
];

export function canadianBands(scale: CanadianScale): readonly CanadianBand[] {
  return scale === "uoft" ? UOFT : UBC_ALBERTA;
}

/** The letters a reader can pick when they have letter grades rather than percentages. */
export function canadianLetters(scale: CanadianScale): readonly string[] {
  if (scale === "uoft") {
    return UOFT.map((band) => band.letter);
  }
  return Object.keys(ALBERTA_POINTS);
}

export function pointsForLetter(
  letter: string,
  scale: CanadianScale
): number | null {
  if (scale === "uoft") {
    return UOFT.find((band) => band.letter === letter)?.points ?? null;
  }
  return ALBERTA_POINTS[letter] ?? null;
}

export function isValidCanadianMark(mark: number): boolean {
  return Number.isFinite(mark) && mark >= 0 && mark <= 100;
}

export function convertCanadianMark(
  mark: number,
  scale: CanadianScale
): CanadianBand | null {
  if (!isValidCanadianMark(mark)) {
    return null;
  }
  return canadianBands(scale).find((band) => mark >= band.min) ?? null;
}

export interface CanadianCourse {
  credits: number;
  /** A percentage mark, or null when the course is entered as a letter. */
  mark: number | null;
  /** A letter grade, used only when `mark` is null. */
  letter: string | null;
}

export interface CanadianSummary {
  gpa: number;
  totalCredits: number;
  courseCount: number;
  /** Credit-weighted average of the percentage marks, or null when none were entered. */
  averageMark: number | null;
  /** The band the average mark falls in, when there is one. */
  averageBand: CanadianBand | null;
}

export function summariseCanadian(
  courses: readonly CanadianCourse[],
  scale: CanadianScale
): CanadianSummary | null {
  let credits = 0;
  let pointSum = 0;
  let count = 0;
  let markCredits = 0;
  let markSum = 0;
  for (const course of courses) {
    if (course.credits > 0) {
      const points =
        course.mark === null
          ? pointsForLetter(course.letter ?? "", scale)
          : (convertCanadianMark(course.mark, scale)?.points ?? null);
      if (points !== null) {
        credits += course.credits;
        pointSum += points * course.credits;
        count += 1;
        if (course.mark !== null) {
          markCredits += course.credits;
          markSum += course.mark * course.credits;
        }
      }
    }
  }
  if (credits <= 0) {
    return null;
  }
  const averageMark =
    markCredits > 0 ? cleanFloat(markSum / markCredits) : null;
  return {
    gpa: cleanFloat(pointSum / credits),
    totalCredits: credits,
    courseCount: count,
    averageMark,
    averageBand:
      averageMark === null ? null : convertCanadianMark(averageMark, scale),
  };
}
